import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { t } from '../i18n/index.ts';
import { dismissInstall, installDismissed, promptInstall, useInstallMode } from '../lib/install.ts';
import { Icon } from './ui.tsx';

/** iOS share icon (square with an arrow), as it looks in Safari's toolbar. */
function ShareGlyph() {
  return (
    <svg className="icon share-glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3v12M8 7l4-4 4 4M7 10H5v11h14V10h-2" />
    </svg>
  );
}

/** A friendly, dismissible card offering to install the app on this device. */
export function InstallPrompt() {
  const mode = useInstallMode();
  const [ready, setReady] = useState(false);
  const [hidden, setHidden] = useState(installDismissed);
  // Wait a moment so the prompt doesn't compete with the page the user just opened.
  useEffect(() => {
    const id = setTimeout(() => setReady(true), 2500);
    return () => clearTimeout(id);
  }, []);
  const show = ready && !hidden && mode !== 'none';
  const close = () => {
    dismissInstall();
    setHidden(true);
  };
  return (
    <AnimatePresence>
      {show && (
        <motion.aside
          className="install-card"
          role="dialog"
          aria-labelledby="install-title"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ type: 'spring', stiffness: 380, damping: 32 }}
        >
          <img src="/icon-192.png" alt="" className="install-icon" width={48} height={48} />
          <div className="install-body">
            <h2 id="install-title">{t('install.title')}</h2>
            <p>{mode === 'ios' ? t('install.bodyIos') : t('install.body')}</p>
            {mode === 'ios' && (
              <ol className="install-steps">
                <li>
                  {t('install.iosStep1')} <ShareGlyph />
                </li>
                <li>{t('install.iosStep2')}</li>
              </ol>
            )}
            <div className="install-actions">
              {mode === 'prompt' && (
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    const ok = await promptInstall();
                    if (!ok) close();
                  }}
                >
                  {t('install.install')}
                </button>
              )}
              <button className="btn btn-ghost" onClick={close}>
                {mode === 'ios' ? t('install.gotIt') : t('install.notNow')}
              </button>
            </div>
          </div>
          <button type="button" className="install-close" aria-label={t('app.close')} onClick={close}>
            <Icon name="close" />
          </button>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
